import { useBackgroundQuery } from "@apollo/client";
import { QueryRef } from "@apollo/client/react/types/types";
import { WasteSubmissionsStaffSummaryQuery, SubmissionStatus, SubmissionMaterializationType } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import {
  ReactNode,
  createContext,
  useContext,
  useDeferredValue,
  useMemo,
} from "react";
import { WASTE_SUBMISSIONS_STAFF_SUMMARY_QUERY } from "./api";

// Default statuses (exclude FILING_COMPLETE and NOT_REPORTING)
// This matches the table's default submissionStatus filter
const DEFAULT_STATUSES = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
];

// Default date range: today to 90 days from now
function getDefaultDateRange() {
  const now = DateTime.now();
  const ninetyDaysFromNow = now.plus({ days: 90 });
  return {
    startDate: now.toISO() ?? "",
    endDate: ninetyDaysFromNow.toISO() ?? "",
  };
}

/**
 * Filter type for the waste fulfillment summary.
 * When provided, overrides the default filter.
 * This is an object (not a string) matching the waste submissions pattern.
 */
export type WasteFulfillmentSummaryFilter = {
  search?: string | null;
  includeOverdue?: boolean;
  onlyOverdue?: boolean;
  startDate?: string;
  endDate?: string;
  /**
   * Filter by materialization type(s).
   * Allows filtering to VIRTUAL_EVENT, MATERIALIZED, ONE_OFF or any combination.
   */
  materializationTypes?: SubmissionMaterializationType[];
};

/**
 * Query variables type for the waste fulfillment summary query.
 */
export type WasteFulfillmentSummaryQueryVariables = {
  search: string | null;
  includeOverdue: boolean;
  onlyOverdue: boolean;
  startDate: string | null;
  endDate: string | null;
  materializationTypes: SubmissionMaterializationType[] | null;
};

/**
 * Build effective query variables by merging external filter with internal defaults.
 * External values take precedence over defaults.
 * Exported for testing.
 */
export function buildEffectiveVariables(
  externalFilter: WasteFulfillmentSummaryFilter | undefined,
  defaultDateRange: { startDate: string; endDate: string },
  defaultSearch: string
): WasteFulfillmentSummaryQueryVariables {
  if (!externalFilter) {
    // No external filter - use internal defaults
    return {
      search: defaultSearch,
      includeOverdue: true,
      onlyOverdue: false,
      startDate: defaultDateRange.startDate,
      endDate: defaultDateRange.endDate,
      materializationTypes: null,
    };
  }

  // Merge external filter with defaults - external values take precedence
  // Use defaults for any missing values to ensure charts always have sensible filters
  return {
    search: externalFilter.search ?? defaultSearch,
    includeOverdue: externalFilter.includeOverdue ?? true,
    onlyOverdue: externalFilter.onlyOverdue ?? false,
    startDate: externalFilter.startDate ?? defaultDateRange.startDate,
    endDate: externalFilter.endDate ?? defaultDateRange.endDate,
    materializationTypes: externalFilter.materializationTypes ?? null,
  };
}

// Export constants for testing
export const __testing = {
  DEFAULT_STATUSES,
  getDefaultDateRange,
};

type WasteFulfillmentSummaryContextType = {
  summaryQueryRef: QueryRef<WasteSubmissionsStaffSummaryQuery>;
  // isPending is true while filters are transitioning (shows stale data with reduced opacity)
  isPending: boolean;
};

const WasteFulfillmentSummaryContext = createContext<
  WasteFulfillmentSummaryContextType | undefined
>(undefined);

export function useWasteFulfillmentSummaryContext() {
  const context = useContext(WasteFulfillmentSummaryContext);
  if (!context) {
    throw new Error(
      "useWasteFulfillmentSummaryContext must be used within a WasteFulfillmentSummaryProvider"
    );
  }
  return context;
}

type WasteFulfillmentSummaryProviderProps = {
  children: ReactNode;
  /**
   * Filter to apply to the summary query.
   * When provided, overrides the default filter.
   * The parent component is responsible for computing the complete filter.
   * When undefined, internal defaults are used.
   */
  filter?: WasteFulfillmentSummaryFilter;
};

export function WasteFulfillmentSummaryProvider({
  children,
  filter: externalFilter,
}: WasteFulfillmentSummaryProviderProps) {
  // Build the effective query variables
  // Merge external filter with internal defaults - external values take precedence
  // This ensures charts always have sensible defaults even if parent doesn't provide all values
  const effectiveVariables = useMemo(() => {
    const defaultDateRange = getDefaultDateRange();
    const defaultSearch = `status:${DEFAULT_STATUSES.join("|")}`;
    return buildEffectiveVariables(externalFilter, defaultDateRange, defaultSearch);
  }, [externalFilter]);

  // Use deferred value to prevent re-suspension when filter changes
  // This keeps showing the old data while new data loads in the background
  // Same pattern as WasteSubmissionsOverviewContext
  const deferredVariables = useDeferredValue(effectiveVariables);

  // isPending is true when filters have changed but deferred value hasn't caught up yet
  // This allows us to show stale data with reduced opacity during transitions
  const isPending = effectiveVariables !== deferredVariables;

  const [summaryQueryRef] = useBackgroundQuery(
    WASTE_SUBMISSIONS_STAFF_SUMMARY_QUERY,
    {
      variables: deferredVariables,
      // IMPORTANT: Use cache-and-network to ensure we always refetch when variables change
      // Without this, Apollo may return stale cached data when filters change
      fetchPolicy: "cache-and-network",
    }
  );

  const value = useMemo(
    () => ({
      summaryQueryRef,
      isPending,
    }),
    [summaryQueryRef, isPending]
  );

  return (
    <WasteFulfillmentSummaryContext.Provider value={value}>
      {children}
    </WasteFulfillmentSummaryContext.Provider>
  );
}
